import React, { createContext, useContext, useState, useEffect } from 'react';
import { loginAdmin } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

const TOKEN_KEY = 'hotelAdminToken';
const USER_KEY = 'hotelAdminUser';
const EXP_KEY = 'hotelAdminTokenExp';
const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

const isTokenExpired = (token) => {
  try {
    // Standard JWS: decode payload directly
    const payload = JSON.parse(atob(token.split('.')[1]));
    return Date.now() >= payload.exp * 1000;
  } catch {
    // Encrypted JWE: payload is not readable — fall back to stored expiry
    const exp = localStorage.getItem(EXP_KEY);
    return !exp || Date.now() >= Number(exp);
  }
};

const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(EXP_KEY);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const savedUser = localStorage.getItem(USER_KEY);
    if (token && savedUser && !isTokenExpired(token)) {
      try {
        setUser(JSON.parse(savedUser));
        setIsAuthenticated(true);
      } catch {
        clearAuth();
      }
    } else if (token) {
      clearAuth();
    }
    setIsLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await loginAdmin(email, password);
      const { token, ...userInfo } = data.data;
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(EXP_KEY, String(Date.now() + TWO_HOURS_MS));
      localStorage.setItem(USER_KEY, JSON.stringify(userInfo));
      setUser(userInfo);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      const message =
        error.response?.data?.error?.[0] ||
        error.response?.data?.message ||
        'Invalid credentials';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    clearAuth();
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
